/**
 * Wishlist store and its two always-on components.
 *
 * Shopify has no native wishlist and a theme cannot write customer metafields,
 * so the list lives in localStorage: it works for guests, costs nothing and
 * needs no app. The per-device limitation is answered by the shareable link
 * built from `encode()` — opening it on another device restores the list.
 *
 * Entries are variant-level ({handle, variantId}) so a wishlisted "5 kg" box
 * can be added to the cart in one click, without a detour through the PDP.
 *
 * `window.CitrusWishlist` is the theme's only global: the heart, the header
 * counter and the wishlist page each live in a different file and must share
 * one store, and duplicating the parse/serialize logic three times would be
 * worse than the single frozen namespace.
 */
(() => {
  const KEY = 'citrus:wishlist';
  const VERSION = 1;
  const MAX_ITEMS = 50;
  const EVENT = 'citrus:wishlist';

  // fallback store for private-mode browsers where localStorage throws
  let memory = null;

  function itemKey(handle, variantId) {
    return `${handle}:${variantId}`;
  }

  /** Handles and ids end up in fetch URLs, so anything odd is dropped. */
  function sanitize(entry) {
    const handle = String(entry.h || '').trim();
    const variantId = String(entry.i || '').trim();
    if (!handle || handle.length > 120) return null;
    if (/[\s/?#&=]/.test(handle)) return null;
    if (!/^\d{1,20}$/.test(variantId)) return null;
    const time = Number(entry.t);
    return { h: handle, i: variantId, t: Number.isFinite(time) ? time : 0 };
  }

  function dedupe(entries) {
    const seen = new Set();
    const result = [];
    entries.forEach((entry) => {
      const clean = sanitize(entry);
      if (!clean) return;
      const key = itemKey(clean.h, clean.i);
      if (seen.has(key)) return;
      seen.add(key);
      result.push(clean);
    });
    return result;
  }

  function read() {
    if (memory) return memory.slice();
    let raw = null;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch {
      memory = [];
      return [];
    }
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      // an unknown version is treated as no list rather than guessed at
      if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.items)) return [];
      return dedupe(parsed.items).sort((a, b) => b.t - a.t);
    } catch {
      return [];
    }
  }

  function write(items, action, key) {
    const next = dedupe(items)
      .sort((a, b) => b.t - a.t)
      .slice(0, MAX_ITEMS);
    if (memory) {
      memory = next;
    } else {
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ v: VERSION, items: next }));
      } catch {
        // quota or private mode: keep the list for this page view at least
        memory = next;
      }
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: next, action, key } }));
    return next;
  }

  function has(handle, variantId) {
    const key = itemKey(handle, variantId);
    return read().some((entry) => itemKey(entry.h, entry.i) === key);
  }

  function add(handle, variantId) {
    const clean = sanitize({ h: handle, i: variantId, t: Date.now() });
    if (!clean) return read();
    const key = itemKey(clean.h, clean.i);
    const items = read().filter((entry) => itemKey(entry.h, entry.i) !== key);
    items.unshift(clean);
    return write(items, 'add', key);
  }

  function remove(handle, variantId) {
    const key = itemKey(handle, variantId);
    return write(
      read().filter((entry) => itemKey(entry.h, entry.i) !== key),
      'remove',
      key
    );
  }

  function toggle(handle, variantId) {
    if (has(handle, variantId)) {
      remove(handle, variantId);
      return false;
    }
    add(handle, variantId);
    return true;
  }

  /** Adds entries without touching the ones already saved. Returns how many were new. */
  function merge(entries) {
    const current = read();
    const known = new Set(current.map((entry) => itemKey(entry.h, entry.i)));
    const fresh = [];
    entries.forEach((entry) => {
      const clean = sanitize({ h: entry.h, i: entry.i, t: entry.t || Date.now() });
      if (!clean) return;
      if (known.has(itemKey(clean.h, clean.i))) return;
      known.add(itemKey(clean.h, clean.i));
      fresh.push(clean);
    });
    if (fresh.length) write(current.concat(fresh), 'merge');
    return fresh.length;
  }

  /** `handle:variantId,handle:variantId` — readable in the URL, no base64. */
  function encode(items) {
    return (items || read()).map((entry) => `${entry.h}:${entry.i}`).join(',');
  }

  function decode(value) {
    if (!value) return [];
    const now = Date.now();
    return dedupe(
      String(value)
        .split(',')
        .map((part, index) => {
          const separator = part.lastIndexOf(':');
          if (separator < 1) return null;
          return {
            h: part.slice(0, separator),
            i: part.slice(separator + 1),
            // keep the shared order: earlier in the string means more recent
            t: now - index,
          };
        })
        .filter(Boolean)
    );
  }

  window.CitrusWishlist = Object.freeze({
    EVENT,
    MAX_ITEMS,
    key: itemKey,
    read,
    count: () => read().length,
    has,
    add,
    remove,
    toggle,
    clear: () => write([], 'clear'),
    merge,
    encode,
    decode,
  });

  // another tab changed the list: re-broadcast so this tab's hearts follow
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items: read(), action: 'sync' } }));
  });

  /**
   * The heart. Reads its state on connect so a saved product never flashes as
   * unsaved, and follows both the store and, on the PDP, the variant picker.
   */
  if (!customElements.get('wishlist-button')) {
    class WishlistButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;

        this.onStoreChange = () => this.sync();
        this.onVariantChange = (event) => {
          const detail = event.detail || {};
          if (String(detail.productId) !== String(this.dataset.productId)) return;
          this.dataset.variantId = detail.variantId || '';
          this.sync();
        };

        this.button.addEventListener('click', () => this.press());
        document.addEventListener(EVENT, this.onStoreChange);
        if (this.dataset.productId) {
          document.addEventListener('citrus:variant:change', this.onVariantChange);
        }

        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(EVENT, this.onStoreChange);
        document.removeEventListener('citrus:variant:change', this.onVariantChange);
      }

      get handle() {
        return this.dataset.handle;
      }

      get variantId() {
        return this.dataset.variantId;
      }

      sync() {
        // The product page lands with no variant selected on purpose, so until
        // the customer picks one there is nothing to save: say so rather than
        // showing a mute disabled heart.
        const ready = Boolean(this.handle && this.variantId);
        this.button.disabled = !ready;

        const active = ready && has(this.handle, this.variantId);
        let label = active ? this.dataset.labelRemove : this.dataset.labelAdd;
        if (!ready) label = this.dataset.labelChoose || label;
        this.button.setAttribute('aria-pressed', String(active));
        this.button.setAttribute('aria-label', label || '');
        this.classList.toggle('is-active', active);
        const text = this.querySelector('[data-wishlist-text]');
        if (text) text.textContent = label || '';
      }

      press() {
        if (!this.handle || !this.variantId) return;
        const added = toggle(this.handle, this.variantId);
        if (!added) return;
        this.button.dispatchEvent(new CustomEvent('citrus:spill', { bubbles: true }));
        this.button.classList.remove('is-popping');
        // reflow so the animation restarts on rapid re-adds
        void this.button.offsetWidth;
        this.button.classList.add('is-popping');
      }
    }
    customElements.define('wishlist-button', WishlistButton);
  }

  /** Header badge. Hidden at zero, like the cart counter. */
  if (!customElements.get('wishlist-count')) {
    class WishlistCount extends HTMLElement {
      connectedCallback() {
        this.onStoreChange = () => this.sync();
        document.addEventListener(EVENT, this.onStoreChange);
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener(EVENT, this.onStoreChange);
      }

      sync() {
        const total = read().length;
        this.textContent = total > 99 ? '99+' : String(total);
        this.hidden = total === 0;
      }
    }
    customElements.define('wishlist-count', WishlistCount);
  }
})();
